<template>
  <div class="tps-page nx-list-page">
    <el-card shadow="never" class="tps-card">
      <div class="tps-toolbar">
        <div class="tps-field">
          <span class="tps-label">业务</span>
          <el-radio-group v-model="businessType" size="small" @change="onBusinessTypeChange">
            <el-radio-button :label="3">汽运入库</el-radio-button>
            <el-radio-button :label="2">汽运出库</el-radio-button>
          </el-radio-group>
        </div>
        <div class="tps-field">
          <span class="tps-label">周起始（周一）</span>
          <el-date-picker
            v-model="weekStart"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择周一所在周"
            size="small"
            @change="onWeekChange"
          />
        </div>
        <div class="tps-field">
          <span class="tps-label">当前客户</span>
          <el-select
            v-model="custNo"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="请选择或搜索客户"
            :remote-method="remoteDept"
            :loading="deptLoading"
            size="small"
            class="tps-cust-select"
            @visible-change="(v) => v && remoteDept('')"
            @focus="preloadDeptIfEmpty"
            @change="onCustNoChange"
          >
            <el-option v-for="d in deptOptions" :key="String(d.id)" :label="d.deptName" :value="String(d.id)" />
          </el-select>
        </div>
        <div class="tps-actions">
          <el-button type="primary" size="small" :disabled="!canLoad" @click="loadWeek">加载周编排</el-button>
          <el-button type="success" size="small" :disabled="!canSave" @click="saveWeekData">保存</el-button>
          <el-button type="warning" size="small" :disabled="!weekStart" @click="openPreciseCreate">精准编排</el-button>
          <el-button size="small" :disabled="!hasSelectedSlot" @click="openPreciseEdit">编辑时段</el-button>
          <el-button size="small" :disabled="!canClearWeek" @click="clearWeek">清空周编排</el-button>
        </div>
      </div>

      <el-alert
        class="tps-hint"
        type="info"
        :closable="false"
        title="可在空白时间轴上拖动新建，也可点击「精准编排」按周几和时间直选批量生成；单击选中，双击删除。同一客户时段不得重叠；同一时刻最多两个不同客户。最小 15 分钟。"
      />

      <div class="week-grid">
        <div class="corner corner--head">日期 / 星期</div>
        <div class="time-ruler">
          <div class="tps-axis">
            <div class="tps-hour-lines" aria-hidden="true">
              <span
                v-for="h in hourBoundaryMarks"
                :key="'rl-' + h"
                class="tps-hour-line"
                :style="{ left: hourLeftPct(h) + '%' }"
              />
            </div>
            <span v-for="h in hourTicks" :key="h" class="tick" :style="{ left: hourLeftPct(h) + '%' }">{{ pad(h) }}:00</span>
          </div>
        </div>
        <template v-for="(label, dayIndex) in dayLabels">
          <div
            :key="'dl-' + dayIndex"
            class="day-label"
            :class="{ 'is-last-day': dayIndex === dayLabels.length - 1 }"
          >
            <div>{{ label.weekday }}</div>
            <small>{{ label.date }}</small>
          </div>
          <div
            :key="'tr-' + dayIndex"
            class="track"
            :class="{ 'is-last-day': dayIndex === dayLabels.length - 1 }"
            :style="{ minHeight: trackMinHeight(dayIndex) + 'px' }"
          >
            <div class="tps-axis" @mousedown.prevent="onTrackDown(dayIndex, $event)">
              <div class="tps-hour-lines" aria-hidden="true">
                <span
                  v-for="h in hourBoundaryMarks"
                  :key="'tg-' + dayIndex + '-' + h"
                  class="tps-hour-line"
                  :style="{ left: hourLeftPct(h) + '%' }"
                />
              </div>
              <div
                v-for="(s, si) in slots[dayIndex]"
                :key="s.id"
                class="slot"
                :class="{ selected: selected.day === dayIndex && selected.id === s.id, invalid: s.invalid }"
                :style="slotStyle(s, si)"
                @mousedown.stop
                @click.stop="selectSlot(dayIndex, s)"
                @dblclick.stop="removeSlot(dayIndex, s.id)"
              >
                <div class="slot-inner">
                  <div class="cust">{{ s.custName || s.custNo }}</div>
                  <div class="tm">{{ formatMin(s.startMin) }}–{{ formatMin(s.endMin) }}</div>
                </div>
              </div>
              <div v-if="ghost && ghost.day === dayIndex" class="slot ghost" :style="ghostStyle(ghost)" />
            </div>
          </div>
        </template>
      </div>
    </el-card>

    <el-drawer
      :visible.sync="preciseDrawerVisible"
      :title="preciseDrawerTitle"
      direction="rtl"
      size="460px"
      custom-class="tps-precise-drawer"
      append-to-body
      :before-close="closePreciseDrawer"
    >
      <div class="precise-body">
        <el-form label-position="top" size="small">
          <el-form-item label="客户" required>
            <el-select
              v-model="preciseForm.custNo"
              filterable
              remote
              clearable
              reserve-keyword
              placeholder="请选择或搜索客户"
              :remote-method="remoteDept"
              :loading="deptLoading"
              class="precise-full"
              @visible-change="(v) => v && remoteDept('')"
              @focus="preloadDeptIfEmpty"
            >
              <el-option v-for="d in deptOptions" :key="String(d.id)" :label="d.deptName" :value="String(d.id)" />
            </el-select>
          </el-form-item>

          <el-form-item label="生成日期" required>
            <div class="precise-days">
              <button
                v-for="d in preciseDayOptions"
                :key="d.index"
                type="button"
                class="precise-day"
                :class="{ active: preciseForm.days.indexOf(d.index) > -1 }"
                :disabled="preciseMode === 'edit'"
                @click="togglePreciseDay(d.index)"
              >
                <span>{{ d.weekday }}</span>
                <small>{{ d.md }}</small>
              </button>
            </div>
          </el-form-item>

          <div class="precise-time-row">
            <el-form-item label="开始时间" required>
              <el-select v-model="preciseForm.startTime" class="precise-time-select" placeholder="开始时间">
                <el-option v-for="t in startTimeOptions" :key="'s-' + t" :label="t" :value="t" />
              </el-select>
            </el-form-item>
            <el-form-item label="结束时间" required>
              <el-select v-model="preciseForm.endTime" class="precise-time-select" placeholder="结束时间">
                <el-option v-for="t in endTimeOptions" :key="'e-' + t" :label="t" :value="t" />
              </el-select>
            </el-form-item>
          </div>

          <el-form-item label="快捷时段">
            <div class="quick-ranges">
              <el-button
                v-for="q in quickRanges"
                :key="q.label"
                size="mini"
                :type="preciseForm.startTime === q.start && preciseForm.endTime === q.end ? 'primary' : ''"
                @click="applyQuickRange(q)"
              >
                {{ q.label }}
              </el-button>
            </div>
          </el-form-item>

          <el-form-item label="备注">
            <el-input v-model="preciseForm.remark" type="textarea" :rows="3" maxlength="200" show-word-limit />
          </el-form-item>
        </el-form>

        <div v-if="preciseConflictMessages.length" class="precise-conflicts">
          <div v-for="msg in preciseConflictMessages" :key="msg">{{ msg }}</div>
        </div>

        <div class="precise-preview">
          <div v-for="row in precisePreviewRows" :key="row.key" class="precise-preview-row">
            <strong>{{ row.weekday }}</strong>
            <span>{{ row.date }} {{ row.custName }} {{ row.timeText }}</span>
            <em :class="{ warn: row.error }">{{ row.error ? '冲突' : '可生成' }}</em>
          </div>
          <div v-if="!precisePreviewRows.length" class="precise-preview-row">
            <strong>未选择</strong>
            <span>请选择至少一个生成日期</span>
            <em class="warn">待选择</em>
          </div>
        </div>
      </div>

      <div class="precise-footer">
        <el-button size="small" @click="resetPreciseForm">重置</el-button>
        <el-button size="small" type="success" @click="applyPreciseSchedule(true)">确定</el-button>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { listByWeek, saveWeek, bladeDeptDistinct } from './api';

const HOURS = 24;
const DAYS = 7;
const MINUTES_DAY = HOURS * 60;
const MAX_SCHEDULE_MIN = MINUTES_DAY - 1;
const MIN_SLOT = 15;
const MAX_CUST = 2;
const ROW_H = 30;
const ROW_GAP = 6;
const TOP_PAD = 8;
const BOT_PAD = 10;
const MIN_DAY_TRACK = 80;
const TIME_OPTIONS = Array.from({ length: MINUTES_DAY / MIN_SLOT }, (_, i) => {
  const m = i * MIN_SLOT;
  return pad(Math.floor(m / 60)) + ':' + pad(m % 60);
}).concat('23:59');
const QUICK_RANGES = [
  { label: '早班 00:00-08:00', start: '00:00', end: '08:00' },
  { label: '中班 08:00-16:00', start: '08:00', end: '16:00' },
  { label: '晚班 16:00-23:59', start: '16:00', end: '23:59' },
  { label: '临时 08:30-10:00', start: '08:30', end: '10:00' }
];

function pad (n) {
  return n < 10 ? '0' + n : '' + n;
}

function addDays (d, n) {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + n);
  return x;
}

function mondayOf (d) {
  const day = d.getDay();
  return addDays(d, day === 0 ? -6 : 1 - day);
}

function toYmd (d) {
  return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

function parseYmd (s) {
  const p = s.split('-');
  return new Date(+p[0], +p[1] - 1, +p[2]);
}

function emptyWeekSlots () {
  return Array.from({ length: DAYS }, () => []);
}

function minFromClientX (el, clientX) {
  const rect = el.getBoundingClientRect();
  const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
  return Math.min(Math.round(((x / rect.width) * MINUTES_DAY) / MIN_SLOT) * MIN_SLOT, MAX_SCHEDULE_MIN);
}

function clampDrag (start, end) {
  let a = Math.min(start, end);
  let b = Math.max(start, end);
  if (b - a < MIN_SLOT) b = a + MIN_SLOT;
  if (b > MAX_SCHEDULE_MIN) b = MAX_SCHEDULE_MIN;
  return [a, b];
}

function intervalOverlap (a, b) {
  return a.start < b.end && b.start < a.end;
}

function normWeekRows (res) {
  const body = res.data;
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  return [];
}

function normDeptList (body) {
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
}

function formatScheduleDt (d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function md (d) {
  return pad(d.getMonth() + 1) + '-' + pad(d.getDate());
}

function minToTime (m) {
  const x = Math.max(0, Math.min(m, MAX_SCHEDULE_MIN));
  return pad(Math.floor(x / 60)) + ':' + pad(x % 60);
}

function timeToMin (t) {
  const p = String(t || '').split(':');
  const min = Number(p[0]) * 60 + Number(p[1]);
  return Math.max(0, Math.min(min, MAX_SCHEDULE_MIN));
}

export default {
  name: 'car-storage/transport-plan-schedule',
  data () {
    return {
      businessType: 3,
      weekStart: '',
      slotsBaselineJson: null,
      slotsDirty: false,
      deptOptions: [],
      deptLoading: false,
      custNo: '',
      custName: '',
      slots: emptyWeekSlots(),
      selected: { day: -1, id: null },
      preciseDrawerVisible: false,
      preciseMode: 'create',
      preciseEditTarget: null,
      preciseForm: {
        custNo: '',
        days: [0],
        startTime: '08:00',
        endTime: '10:00',
        remark: ''
      },
      quickRanges: QUICK_RANGES,
      drag: null,
      ghost: null,
      boundMove: null,
      boundUp: null
    };
  },
  computed: {
    hourBoundaryMarks () {
      return Array.from({ length: HOURS + 1 }, (_, h) => h);
    },
    hourTicks () {
      return this.hourBoundaryMarks.slice(0, -1);
    },
    mondayDate () {
      return this.weekStart ? mondayOf(parseYmd(this.weekStart)) : null;
    },
    dayLabels () {
      if (!this.mondayDate) return [];
      const w = ['周一', '周二', '周三', '周四', '周五', '周六', '周日'];
      return w.map((weekday, i) => ({
        weekday,
        date: toYmd(addDays(this.mondayDate, i))
      }));
    },
    canLoad () {
      return !!this.weekStart;
    },
    slotsSnapshotJson () {
      return this.serializeSlots(this.slots);
    },
    hasAnySlot () {
      return this.slots.some((d) => d.length > 0);
    },
    canSave () {
      if (!this.weekStart) return false;
      if (this.slotsBaselineJson === null) return this.hasAnySlot;
      return this.slotsDirty || this.slotsSnapshotJson !== this.slotsBaselineJson;
    },
    canClearWeek () {
      return !!this.weekStart && this.hasAnySlot;
    },
    hasSelectedSlot () {
      return this.selected.day > -1 && !!this.findSlot(this.selected.day, this.selected.id);
    },
    startTimeOptions () {
      return TIME_OPTIONS.slice(0, -1);
    },
    endTimeOptions () {
      return TIME_OPTIONS.slice(1);
    },
    preciseDrawerTitle () {
      return this.preciseMode === 'edit' ? '编辑时段' : '精准编排';
    },
    preciseDayOptions () {
      return this.dayLabels.map((d, index) => ({
        index,
        weekday: d.weekday,
        date: d.date,
        md: md(parseYmd(d.date))
      }));
    },
    preciseStartMin () {
      return timeToMin(this.preciseForm.startTime);
    },
    preciseEndMin () {
      return timeToMin(this.preciseForm.endTime);
    },
    preciseCustName () {
      return this.resolveDeptName(this.preciseForm.custNo);
    },
    precisePreviewRows () {
      if (!this.preciseForm.days.length) return [];
      return this.preciseForm.days.map((dayIndex) => {
        const day = this.preciseDayOptions.find((d) => d.index === dayIndex) || {};
        const error = this.validatePreciseDay(dayIndex);
        return {
          key: dayIndex + '-' + this.preciseForm.custNo + '-' + this.preciseForm.startTime + '-' + this.preciseForm.endTime,
          weekday: day.weekday || '',
          date: day.date || '',
          custName: this.preciseCustName || this.preciseForm.custNo || '未选择客户',
          timeText: this.preciseForm.startTime + '-' + this.preciseForm.endTime,
          error
        };
      });
    },
    preciseConflictMessages () {
      return this.precisePreviewRows.filter((row) => row.error).map((row) => row.weekday + '：' + row.error);
    }
  },
  created () {
    this.weekStart = toYmd(mondayOf(new Date()));
  },
  mounted () {
    this.remoteDept('');
    this.loadWeek()
  },
  beforeDestroy () {
    this.unbindGlobalDrag();
  },
  methods: {
    pad,
    hourLeftPct (h) {
      return (h / HOURS) * 100;
    },
    onBusinessTypeChange () {
      this.slotsBaselineJson = null;
      this.slotsDirty = false;
      this.clearSlots();
      this.resetPreciseForm();
    },
    onWeekChange () {
      if (this.weekStart) this.weekStart = toYmd(mondayOf(parseYmd(this.weekStart)));
      this.slotsBaselineJson = null;
      this.slotsDirty = false;
      this.clearSlots();
      this.resetPreciseForm();
    },
    clearSlots () {
      this.slots = emptyWeekSlots();
      this.selected = { day: -1, id: null };
    },
    serializeSlots (slots) {
      const norm = slots.map((day) =>
        day.slice().sort((a, b) =>
          a.startMin - b.startMin || a.endMin - b.endMin || String(a.custNo).localeCompare(String(b.custNo))
        ).map((s) => ({
          custNo: String(s.custNo),
          custName: s.custName || '',
          startMin: s.startMin,
          endMin: s.endMin,
          row: s.row != null ? s.row : 0,
          remark: s.remark || ''
        }))
      );
      return JSON.stringify(norm);
    },
    remoteDept (q) {
      this.deptLoading = true;
      const params = {};
      const name = q != null ? String(q).trim() : '';
      if (name) params.deptName = name;
      bladeDeptDistinct(params)
        .then((res) => { this.deptOptions = normDeptList(res.data); })
        .finally(() => { this.deptLoading = false; });
    },
    preloadDeptIfEmpty () {
      if (!this.deptOptions.length) this.remoteDept('');
    },
    onCustNoChange (val) {
      if (!val) {
        this.custName = '';
        return;
      }
      const row = this.deptOptions.find((d) => String(d.id) === String(val));
      this.custName = (row && row.deptName) || '';
    },
    resolveDeptName (val) {
      if (!val) return '';
      const row = this.deptOptions.find((d) => String(d.id) === String(val));
      return (row && row.deptName) || '';
    },
    loadWeek () {
      if (!this.canLoad) return Promise.resolve();
      return listByWeek({
        businessType: this.businessType,
        weekStart: this.weekStart
      }).then((res) => {
        this.applyRowsToSlots(normWeekRows(res));
        this.$message.success('已加载');
      });
    },
    applyRowsToSlots (rows) {
      const next = emptyWeekSlots();
      if (!this.mondayDate) {
        this.slots = next;
        return;
      }
      const base = this.mondayDate.getTime();
      rows.forEach((r) => {
        const raw = r.scheduleDate;
        const sd = raw
          ? (typeof raw === 'string' ? raw.split(' ')[0] : toYmd(new Date(raw)))
          : '';
        if (!sd) return;
        const dayIndex = Math.round((parseYmd(sd).getTime() - base) / 86400000);
        if (dayIndex < 0 || dayIndex > 6) return;
        const sh = new Date(r.startDatetime);
        const eh = new Date(r.endDatetime);
        const endDateText = toYmd(eh);
        const endMin = endDateText > sd ? MAX_SCHEDULE_MIN : Math.min(eh.getHours() * 60 + eh.getMinutes(), MAX_SCHEDULE_MIN);
        next[dayIndex].push({
          id: 'r' + r.id,
          custNo: String(r.custNo),
          custName: r.custName || '',
          startMin: sh.getHours() * 60 + sh.getMinutes(),
          endMin,
          row: r.displayRow != null ? r.displayRow : 0,
          remark: r.remark || '',
          invalid: false
        });
      });
      for (let d = 0; d < DAYS; d++) this.assignRows(next[d]);
      this.slots = next;
      this.revalidateAll();
      this.slotsBaselineJson = this.serializeSlots(this.slots);
      this.slotsDirty = false;
    },
    buildSaveItems () {
      if (!this.mondayDate) return [];
      const items = [];
      for (let d = 0; d < DAYS; d++) {
        const ymd = toYmd(addDays(this.mondayDate, d));
        this.slots[d].forEach((s) => {
          const start = new Date(parseYmd(ymd));
          start.setHours(0, 0, 0, 0);
          start.setMinutes(s.startMin);
          const end = new Date(parseYmd(ymd));
          end.setHours(0, 0, 0, 0);
          end.setMinutes(s.endMin);
          items.push({
            scheduleDate: ymd,
            startDatetime: formatScheduleDt(start),
            endDatetime: formatScheduleDt(end),
            custNo: s.custNo,
            custName: s.custName || '',
            displayRow: s.row != null ? s.row : 0,
            status: 1,
            remark: s.remark || ''
          });
        });
      }
      return items;
    },
    saveWeekData () {
      if (!this.canSave) return;
      this.revalidateAll();
      if (this.slots.some((day) => day.some((s) => s.endMin > MAX_SCHEDULE_MIN))) {
        this.$message.error('入厂编排结束时间不能超过23:59');
        return;
      }
      if (this.slots.some((day) => day.some((s) => s.invalid))) {
        this.$message.error('存在违反规则的时段，请调整后保存');
        return;
      }
      saveWeek({
        transportPlanId: null,
        businessType: this.businessType,
        weekStart: this.weekStart,
        items: this.buildSaveItems()
      }).then(() => {
        this.$message.success('保存成功');
        return this.loadWeek();
      });
    },
    clearWeek () {
      if (!this.weekStart) return;
      this.$confirm('确定清空本周编排？保存后生效。', '提示', { type: 'warning' })
        .then(() => {
          this.clearSlots();
          return saveWeek({
            transportPlanId: null,
            businessType: this.businessType,
            weekStart: this.weekStart,
            items: []
          });
        })
        .then(() => {
          this.$message.success('已清空');
          return this.loadWeek();
        })
        .catch(() => {});
    },
    trackMinHeight (dayIndex) {
      const n = this.rowCount(dayIndex);
      const h = TOP_PAD + n * ROW_H + Math.max(0, n - 1) * ROW_GAP + BOT_PAD;
      return Math.max(h, MIN_DAY_TRACK);
    },
    rowCount (dayIndex) {
      const list = this.slots[dayIndex];
      if (!list.length) return 1;
      return Math.max(...list.map((s) => (s.row != null ? s.row : 0))) + 1;
    },
    slotStyle (s, zBase) {
      const k = 100 / MINUTES_DAY;
      return {
        left: s.startMin * k + '%',
        width: (s.endMin - s.startMin) * k + '%',
        height: ROW_H + 'px',
        top: TOP_PAD + (s.row || 0) * (ROW_H + ROW_GAP) + 'px',
        zIndex: 3 + (zBase % 3)
      };
    },
    ghostStyle (g) {
      const k = 100 / MINUTES_DAY;
      const a = Math.min(g.a, g.b);
      const b = Math.max(g.a, g.b);
      return {
        left: a * k + '%',
        width: (b - a) * k + '%',
        height: ROW_H + 'px',
        top: TOP_PAD + 'px',
        zIndex: 2
      };
    },
    formatMin (m) {
      return minToTime(m);
    },
    nextId () {
      return 's' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
    },
    onTrackDown (dayIndex, e) {
      if (!this.custNo) {
        this.$message.warning('请先选择当前客户');
        return;
      }
      const axis = e.currentTarget;
      const start = minFromClientX(axis, e.clientX);
      this.drag = { dayIndex, axis, start, cur: start };
      this.ghost = { day: dayIndex, a: start, b: start };
      this.boundMove = (ev) => this.onGlobalMove(ev);
      this.boundUp = () => this.onGlobalUp();
      window.addEventListener('mousemove', this.boundMove);
      window.addEventListener('mouseup', this.boundUp);
    },
    onGlobalMove (e) {
      if (!this.drag) return;
      this.drag.cur = minFromClientX(this.drag.axis, e.clientX);
      const [a, b] = clampDrag(this.drag.start, this.drag.cur);
      this.ghost = { day: this.drag.dayIndex, a, b };
    },
    onGlobalUp () {
      if (!this.drag) {
        this.unbindGlobalDrag();
        return;
      }
      const { dayIndex, start, cur } = this.drag;
      const [a, b] = clampDrag(start, cur);
      if (b > a && this.custNo) {
        const probe = {
          id: null,
          custNo: this.custNo,
          custName: this.custName || '',
          startMin: a,
          endMin: b,
          row: 0,
          remark: '',
          invalid: false
        };
        const err = this.validateSlot(dayIndex, null, probe);
        if (!err) {
          probe.id = this.nextId();
          this.slots[dayIndex].push(probe);
          this.assignRows(this.slots[dayIndex]);
          this.revalidateAll();
          this.slotsDirty = true;
        } else {
          this.$message.error(err === 'sameCust' ? '同一客户时段不能重叠' : '该时段已有两个不同客户，不能再加');
        }
      }
      this.drag = null;
      this.ghost = null;
      this.unbindGlobalDrag();
    },
    unbindGlobalDrag () {
      if (this.boundMove) window.removeEventListener('mousemove', this.boundMove);
      if (this.boundUp) window.removeEventListener('mouseup', this.boundUp);
      this.boundMove = this.boundUp = null;
    },
    selectSlot (dayIndex, s) {
      this.selected = { day: dayIndex, id: s.id };
    },
    findSlot (dayIndex, id) {
      if (dayIndex < 0 || !id || !this.slots[dayIndex]) return null;
      return this.slots[dayIndex].find((s) => s.id === id) || null;
    },
    openPreciseCreate () {
      this.preciseMode = 'create';
      this.preciseEditTarget = null;
      this.preciseForm = {
        custNo: this.custNo || '',
        days: [0],
        startTime: '08:00',
        endTime: '10:00',
        remark: ''
      };
      this.preciseDrawerVisible = true;
      this.preloadDeptIfEmpty();
    },
    openPreciseEdit () {
      const s = this.findSlot(this.selected.day, this.selected.id);
      if (!s) return;
      this.preciseMode = 'edit';
      this.preciseEditTarget = { day: this.selected.day, id: s.id };
      this.preciseForm = {
        custNo: s.custNo,
        days: [this.selected.day],
        startTime: minToTime(s.startMin),
        endTime: minToTime(s.endMin),
        remark: s.remark || ''
      };
      this.preciseDrawerVisible = true;
      this.preloadDeptIfEmpty();
    },
    closePreciseDrawer (done) {
      this.preciseDrawerVisible = false;
      if (typeof done === 'function') done();
    },
    resetPreciseForm () {
      if (this.preciseMode === 'edit' && this.preciseEditTarget) {
        const s = this.findSlot(this.preciseEditTarget.day, this.preciseEditTarget.id);
        if (s) {
          this.preciseForm = {
            custNo: s.custNo,
            days: [this.preciseEditTarget.day],
            startTime: minToTime(s.startMin),
            endTime: minToTime(s.endMin),
            remark: s.remark || ''
          };
          return;
        }
      }
      this.preciseForm = {
        custNo: this.custNo || '',
        days: [0],
        startTime: '08:00',
        endTime: '10:00',
        remark: ''
      };
    },
    togglePreciseDay (dayIndex) {
      if (this.preciseMode === 'edit') return;
      const next = this.preciseForm.days.slice();
      const idx = next.indexOf(dayIndex);
      if (idx > -1) next.splice(idx, 1);
      else next.push(dayIndex);
      next.sort((a, b) => a - b);
      this.preciseForm.days = next;
    },
    applyQuickRange (q) {
      this.preciseForm.startTime = q.start;
      this.preciseForm.endTime = q.end;
    },
    validatePreciseBase () {
      if (!this.weekStart) return '请先选择周起始';
      if (!this.preciseForm.custNo) return '请选择客户';
      if (!this.preciseForm.days.length) return '请选择生成日期';
      if (this.preciseEndMin <= this.preciseStartMin) return '结束时间必须晚于开始时间';
      return '';
    },
    validatePreciseDay (dayIndex) {
      const baseError = this.validatePreciseBase();
      if (baseError) return baseError;
      const excludeId = this.preciseMode === 'edit' && this.preciseEditTarget && this.preciseEditTarget.day === dayIndex
        ? this.preciseEditTarget.id
        : null;
      const probe = {
        custNo: this.preciseForm.custNo,
        startMin: this.preciseStartMin,
        endMin: this.preciseEndMin
      };
      const err = this.validateSlot(dayIndex, excludeId, probe);
      if (err === 'sameCust') return '同一客户时段不能重叠';
      if (err === 'maxCust') return '该时段已有两个不同客户，不能再加';
      return '';
    },
    applyPreciseSchedule (closeAfter) {
      const baseError = this.validatePreciseBase();
      if (baseError) {
        this.$message.error(baseError);
        return;
      }
      if (this.preciseConflictMessages.length) {
        this.$message.error('存在冲突，未生成时段');
        return;
      }
      const custName = this.preciseCustName || '';
      if (this.preciseMode === 'edit') {
        const s = this.preciseEditTarget ? this.findSlot(this.preciseEditTarget.day, this.preciseEditTarget.id) : null;
        if (!s) {
          this.$message.error('未找到待编辑时段');
          return;
        }
        s.custNo = this.preciseForm.custNo;
        s.custName = custName;
        s.startMin = this.preciseStartMin;
        s.endMin = this.preciseEndMin;
        s.remark = this.preciseForm.remark || '';
        this.assignRows(this.slots[this.preciseEditTarget.day]);
        this.revalidateAll();
        this.slotsDirty = true;
        this.$message.success('时段已更新');
      } else {
        this.preciseForm.days.forEach((dayIndex) => {
          this.slots[dayIndex].push({
            id: this.nextId(),
            custNo: this.preciseForm.custNo,
            custName,
            startMin: this.preciseStartMin,
            endMin: this.preciseEndMin,
            row: 0,
            remark: this.preciseForm.remark || '',
            invalid: false
          });
          this.assignRows(this.slots[dayIndex]);
        });
        this.revalidateAll();
        this.slotsDirty = true;
        this.$message.success('已生成 ' + this.preciseForm.days.length + ' 条时段');
      }
      if (closeAfter) this.preciseDrawerVisible = false;
    },
    removeSlot (dayIndex, id) {
      this.$set(this.slots, dayIndex, this.slots[dayIndex].filter((x) => x.id !== id));
      if (this.selected.day === dayIndex && this.selected.id === id) this.selected = { day: -1, id: null };
      this.assignRows(this.slots[dayIndex]);
      this.revalidateAll();
      this.slotsDirty = true;
    },
    assignRows (daySlots) {
      const sorted = daySlots.slice().sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
      const rowLastEnd = [];
      sorted.forEach((s) => {
        for (let r = 0; r < 48; r++) {
          const le = rowLastEnd[r];
          if (le == null || le <= s.startMin) {
            rowLastEnd[r] = s.endMin;
            s.row = r;
            break;
          }
        }
      });
    },
    validateSlot (dayIndex, excludeId, probe) {
      const daySlots = this.slots[dayIndex];
      const p = { start: probe.startMin, end: probe.endMin };
      for (let i = 0; i < daySlots.length; i++) {
        const s = daySlots[i];
        if (excludeId != null && s.id === excludeId) continue;
        if (s.custNo === probe.custNo && intervalOverlap(p, { start: s.startMin, end: s.endMin })) {
          return 'sameCust';
        }
      }
      const list = daySlots
        .filter((s) => excludeId == null || s.id !== excludeId)
        .map((s) => ({ start: s.startMin, end: s.endMin, custNo: s.custNo }));
      list.push({ start: probe.startMin, end: probe.endMin, custNo: probe.custNo });
      for (let t = 0; t < MINUTES_DAY; t += MIN_SLOT) {
        const seen = {};
        let cnt = 0;
        for (let j = 0; j < list.length; j++) {
          const sl = list[j];
          if (t >= sl.start && t < sl.end && !seen[sl.custNo]) {
            seen[sl.custNo] = true;
            cnt++;
          }
        }
        if (cnt > MAX_CUST) return 'maxCust';
      }
      return null;
    },
    revalidateAll () {
      for (let d = 0; d < DAYS; d++) {
        this.slots[d].forEach((s) => { s.invalid = !!this.validateSlot(d, s.id, s); });
      }
    }
  }
};
</script>

<style scoped>
.tps-page {
  box-sizing: border-box;
  color: #303133;
}

.tps-card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
}

.tps-card ::v-deep .el-card__body {
  padding: 16px 18px;
  box-sizing: border-box;
}

.tps-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px 20px;
  margin-bottom: 12px;
}

.tps-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tps-label {
  font-size: 12px;
  color: #909399;
}

.tps-cust-select {
  min-width: 220px;
}

.tps-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-left: auto;
}

.tps-hint {
  margin-bottom: 12px;
  border-radius: 4px;
}

.tps-hint ::v-deep .el-alert__title {
  font-size: 13px;
  line-height: 1.5;
}

.week-grid {
  --tps-gutter: 108px;
  --tps-axis-inset: 12px;
  /* 左侧略大，给 0 点刻度文字留空，并与轨道共用保证对齐 */
  --tps-axis-inset-left: 24px;
  --tps-line: #ebeef5;
  --tps-head-bg: #fafafa;
  --tps-track-bg: #f5f7fa;
  display: grid;
  grid-template-columns: var(--tps-gutter) minmax(0, 1fr);
  grid-template-rows: auto repeat(7, minmax(72px, auto));
  border: 1px solid var(--tps-line);
  border-radius: 6px;
  background: #fff;
}

.corner,
.day-label {
  min-width: 0;
  box-sizing: border-box;
  border-right: 1px solid var(--tps-line);
}

.corner {
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  display: flex;
  align-items: center;
}

.corner--head {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--tps-head-bg);
  border-bottom: 1px solid var(--tps-line);
}

.time-ruler {
  position: sticky;
  top: 0;
  z-index: 1;
  min-width: 0;
  min-height: 38px;
  padding: 6px 0 8px;
  box-sizing: border-box;
  background: var(--tps-head-bg);
  border-bottom: 1px solid var(--tps-line);
}

.tps-axis {
  position: relative;
  overflow: hidden;
}

.time-ruler .tps-axis {
  margin: 0 var(--tps-axis-inset) 0 var(--tps-axis-inset-left);
  min-height: 24px;
  /* 刻度 translateX(-50%)，0 点在左缘时前半段会被父级裁切，需允许溢出 */
  overflow: visible;
}

.track .tps-axis {
  position: absolute;
  left: var(--tps-axis-inset-left);
  right: var(--tps-axis-inset);
  top: 0;
  bottom: 0;
  cursor: crosshair;
  /* 与顶部刻度轴一致：允许时段标签溢出绘制，避免被裁切 */
  overflow: visible;
}

.tps-hour-lines {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.tps-hour-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--tps-line);
  transform: translateX(-50%);
}

.tick {
  position: absolute;
  top: 8px;
  z-index: 1;
  transform: translateX(-50%);
  font-size: 11px;
  font-weight: 500;
  color: #909399;
  white-space: nowrap;
}

.day-label {
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  border-bottom: 1px solid var(--tps-line);
  background: var(--tps-head-bg);
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.day-label.is-last-day,
.track.is-last-day {
  border-bottom: none;
}

.day-label small {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #909399;
}

.track {
  min-width: 0;
  min-height: 0;
  position: relative;
  border-bottom: 1px solid var(--tps-line);
  background: var(--tps-track-bg);
}

.slot {
  position: absolute;
  border-radius: 4px;
  box-sizing: border-box;
  border: 1px solid rgba(25, 137, 250, 0.35);
  background: linear-gradient(180deg, #66b1ff, #409eff);
  color: #fff;
  font-size: 11px;
  cursor: pointer;
  /* 窄块时仍可读：文字可伸出色块，由轨道 axis 的 overflow:visible 配合 */
  overflow: visible;
  box-shadow: 0 1px 3px rgba(64, 158, 255, 0.25);
}

.slot.invalid {
  border-color: #f56c6c;
  box-shadow: 0 0 0 1px rgba(245, 108, 108, 0.4);
}

.slot.selected {
  z-index: 20 !important;
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #f56c6c;
}

.slot-inner {
  padding: 1px 4px;
  line-height: 1.15;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.12);
}

.slot .cust {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.slot .tm {
  font-size: 10px;
  opacity: 0.92;
  margin-top: 0;
  white-space: nowrap;
}

.slot.ghost {
  background: rgba(64, 158, 255, 0.2);
  border: 2px dashed #409eff;
  pointer-events: none;
  box-shadow: none;
}

.tps-page ::v-deep .tps-precise-drawer .el-drawer__header {
  margin-bottom: 0;
  padding: 16px 18px;
  border-bottom: 1px solid #ebeef5;
  color: #303133;
  font-weight: 600;
}

.tps-page ::v-deep .tps-precise-drawer .el-drawer__body {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.precise-body {
  flex: 1;
  padding: 16px 18px;
  overflow: auto;
  box-sizing: border-box;
}

.precise-full {
  width: 100%;
}

.precise-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 7px;
}

.precise-day {
  height: 46px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
}

.precise-day small {
  color: #909399;
  font-size: 11px;
  line-height: 12px;
}

.precise-day.active {
  border-color: #409eff;
  background: #ecf5ff;
  color: #409eff;
  font-weight: 600;
}

.precise-day.active small {
  color: #409eff;
}

.precise-day:disabled {
  cursor: not-allowed;
}

.precise-time-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.precise-time-select {
  width: 100%;
}

.quick-ranges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.precise-conflicts {
  margin-top: 12px;
  padding: 9px 10px;
  border: 1px solid #f3d19e;
  border-radius: 6px;
  background: #fdf6ec;
  color: #9a6e3a;
  font-size: 12px;
  line-height: 20px;
}

.precise-preview {
  margin-top: 12px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
}

.precise-preview-row {
  display: grid;
  grid-template-columns: 74px 1fr 58px;
  gap: 8px;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid #ebeef5;
  color: #606266;
  font-size: 12px;
}

.precise-preview-row:last-child {
  border-bottom: none;
}

.precise-preview-row strong {
  color: #303133;
}

.precise-preview-row span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.precise-preview-row em {
  height: 22px;
  line-height: 22px;
  border-radius: 11px;
  background: #f0f9eb;
  color: #529b2e;
  text-align: center;
  font-style: normal;
}

.precise-preview-row em.warn {
  background: #fdf6ec;
  color: #b88230;
}

.precise-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}

@media (max-width: 768px) {
  .tps-actions {
    margin-left: 0;
    width: 100%;
  }

  .precise-days {
    grid-template-columns: repeat(4, 1fr);
  }

  .precise-time-row {
    grid-template-columns: 1fr;
    gap: 0;
  }
}
</style>
